import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  RefreshCw,    // Revalidation
  Star,         // Review
  Shield,       // Reputation
  MessageSquare,// Response
  Gift,         // Reward
  Clock,        // Reliability
  Award,        // Recognition
  Scale         // Regulation
} from "lucide-react";

/**
 * The SelleTrust Assurance - 8 R's Trust Framework
 * Revalidation → Review → Reputation → Response → Reward → Reliability → Recognition → Regulation
 */

const eightRs = [
  {
    id: "revalidation",
    name: "Revalidation",
    icon: RefreshCw,
    color: "from-blue-500 to-blue-600",
    bgColor: "bg-blue-50",
    borderColor: "border-blue-200",
    textColor: "text-blue-700",
    description: "Continuously verify and validate business information accuracy",
    details: [
      "Real-time data verification",
      "Cross-platform consistency checks",
      "Automated accuracy monitoring",
      "Regular re-certification"
    ]
  },
  {
    id: "review",
    name: "Review",
    icon: Star,
    color: "from-yellow-500 to-amber-500",
    bgColor: "bg-yellow-50",
    borderColor: "border-yellow-200",
    textColor: "text-yellow-700",
    description: "Aggregate and analyze reviews from all major platforms",
    details: [
      "50+ review platforms monitored",
      "Sentiment analysis",
      "Review authenticity scoring",
      "Trend identification"
    ]
  },
  {
    id: "reputation",
    name: "Reputation",
    icon: Shield,
    color: "from-green-500 to-emerald-500",
    bgColor: "bg-green-50",
    borderColor: "border-green-200",
    textColor: "text-green-700",
    description: "Build and protect your online reputation score",
    details: [
      "Comprehensive reputation scoring",
      "Competitive benchmarking",
      "Brand sentiment tracking",
      "Crisis early warning"
    ]
  },
  {
    id: "response",
    name: "Response",
    icon: MessageSquare,
    color: "from-purple-500 to-violet-500",
    bgColor: "bg-purple-50",
    borderColor: "border-purple-200",
    textColor: "text-purple-700",
    description: "AI-powered responses to engage customers effectively",
    details: [
      "AI-generated response suggestions",
      "Tone and brand voice matching",
      "Multi-platform response management",
      "Response time optimization"
    ]
  },
  {
    id: "reward",
    name: "Reward",
    icon: Gift,
    color: "from-pink-500 to-rose-500",
    bgColor: "bg-pink-50",
    borderColor: "border-pink-200",
    textColor: "text-pink-700",
    description: "Incentivize customers to share their positive experiences",
    details: [
      "Review request campaigns",
      "Customer loyalty programs",
      "Referral tracking",
      "Engagement rewards"
    ]
  },
  {
    id: "reliability",
    name: "Reliability",
    icon: Clock,
    color: "from-cyan-500 to-teal-500",
    bgColor: "bg-cyan-50",
    borderColor: "border-cyan-200",
    textColor: "text-cyan-700",
    description: "Demonstrate consistent, dependable business practices",
    details: [
      "Response time tracking",
      "Service consistency metrics",
      "Availability monitoring",
      "Trust score maintenance"
    ]
  },
  {
    id: "recognition",
    name: "Recognition",
    icon: Award,
    color: "from-orange-500 to-amber-600",
    bgColor: "bg-orange-50",
    borderColor: "border-orange-200",
    textColor: "text-orange-700",
    description: "Earn badges, certifications, and trust indicators",
    details: [
      "Trust badges & certifications",
      "Industry recognition",
      "Achievement milestones",
      "Public trust indicators"
    ]
  },
  {
    id: "regulation",
    name: "Regulation",
    icon: Scale,
    color: "from-indigo-500 to-indigo-600",
    bgColor: "bg-indigo-50",
    borderColor: "border-indigo-200",
    textColor: "text-indigo-700",
    description: "Ensure compliance with industry standards and regulations",
    details: [
      "Compliance monitoring",
      "Industry regulation tracking",
      "Policy adherence verification",
      "Audit readiness reports"
    ]
  }
];

export const SelleTrustAssurance = ({ compact = false }) => {
  if (compact) {
    return (
      <div className="py-8">
        <div className="text-center mb-6">
          <Badge className="bg-blue-100 text-blue-700 mb-3">THE SELLETRUST ASSURANCE</Badge>
          <h3 className="text-2xl font-bold text-slate-900">Build Trust Through the 8 R's</h3>
        </div>
        <div className="flex flex-wrap justify-center gap-3">
          {eightRs.map((r, index) => {
            const Icon = r.icon;
            return (
              <div 
                key={r.id}
                className={`flex items-center gap-2 px-4 py-2 rounded-full ${r.bgColor} ${r.borderColor} border`}
              >
                <Icon className={`w-4 h-4 ${r.textColor}`} />
                <span className={`font-medium ${r.textColor}`}>{r.name}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <section className="py-16 bg-gradient-to-b from-slate-50 to-white" data-testid="selletrust-assurance">
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="bg-blue-600 text-white px-4 py-1.5 text-sm mb-4">
            THE SELLETRUST ASSURANCE
          </Badge>
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Build Trust Through the <span className="text-blue-600">8 R's</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Our comprehensive framework ensures your business earns and maintains 
            the trust of every customer, every time.
          </p>
        </div>

        {/* 8 R's Flow Diagram */}
        <div className="relative mb-16">
          {/* Connection Line */}
          <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-blue-200 via-purple-200 to-indigo-200 transform -translate-y-1/2 z-0" />
          
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
            {eightRs.map((r, index) => {
              const Icon = r.icon;
              return (
                <div key={r.id} className="relative z-10">
                  <Card className={`border-2 ${r.borderColor} ${r.bgColor} hover:shadow-lg transition-all hover:-translate-y-1`}>
                    <CardContent className="p-3 text-center">
                      <div className={`w-10 h-10 mx-auto rounded-full bg-gradient-to-br ${r.color} flex items-center justify-center mb-2 shadow-lg`}>
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      <h3 className={`font-bold text-sm ${r.textColor}`}>{r.name}</h3>
                    </CardContent>
                  </Card>
                  {/* Number indicator */}
                  <div className={`absolute -top-2 -right-2 w-5 h-5 rounded-full bg-gradient-to-br ${r.color} text-white text-xs font-bold flex items-center justify-center shadow`}>
                    {index + 1}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detailed Cards */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {eightRs.map((r) => {
            const Icon = r.icon;
            return (
              <Card key={r.id} className={`border ${r.borderColor} hover:shadow-md transition-all`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-9 h-9 rounded-lg bg-gradient-to-br ${r.color} flex items-center justify-center`}>
                      <Icon className="w-4 h-4 text-white" />
                    </div>
                    <h3 className="font-bold text-slate-900">{r.name}</h3>
                  </div>
                  <p className="text-sm text-slate-600 mb-3">{r.description}</p>
                  <ul className="space-y-1.5">
                    {r.details.map((detail, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-xs text-slate-500">
                        <div className={`w-1 h-1 rounded-full bg-gradient-to-r ${r.color}`} />
                        {detail}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <div className="inline-block p-6 rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-xl">
            <p className="text-xl font-bold mb-2">
              "That's the SelleTrust Assurance"
            </p>
            <p className="text-blue-100">
              Comprehensive trust building for modern businesses
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export const AssuranceBadge = ({ size = "md" }) => {
  const sizes = {
    sm: "p-2 text-xs",
    md: "p-3 text-sm",
    lg: "p-4 text-base"
  };
  
  return (
    <div className={`inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg ${sizes[size]} shadow-lg`}>
      <Shield className={size === "sm" ? "w-4 h-4" : size === "md" ? "w-5 h-5" : "w-6 h-6"} />
      <span className="font-bold">SelleTrust Assured</span>
    </div>
  );
};

export default SelleTrustAssurance;
